// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../helios/services/api';
import { getArtifact } from '../services/sopFactoryService';
import { toProcedureDetail } from './toProcedureDetail';
import type { Artifact, ProcedureDetail } from '../types/procedures';

export interface ProcedureView {
  procedure: ProcedureDetail | null;
  /** The raw envelope `procedure` was mapped from — `fromProcedureDetail()`
   *  needs it as a save's base, to preserve fields the guided form never
   *  edits (payload_ref, relationships, provenance, approval, ...). */
  artifact: Artifact | null;
  isLoading: boolean;
  error: string | null;
  /** The BFF answered 404: the artifact doesn't exist in SOP Factory (as
   *  opposed to a transient/other failure), so the screen can show a
   *  dedicated "not found" state instead of a generic load error. */
  notFound: boolean;
}

/**
 * Single-artifact fetch for the procedure view/create screens. `procedure` is
 * memoized on `query.data` — callers (e.g. `ProcedureViewPage`) sync it into
 * local editable state via a `useEffect` keyed on this value, so a fresh
 * object on every render (from calling `toProcedureDetail` unmemoized) would
 * re-trigger that effect on every keystroke and wipe in-progress edits.
 */
export function useProcedure(artifactId: string | undefined, version?: string): ProcedureView {
  const query = useQuery({
    queryKey: ['admin-center', 'procedure', artifactId, version ?? null],
    queryFn: () => getArtifact(artifactId as string, version),
    enabled: Boolean(artifactId),
  });

  const procedure = useMemo(
    () => (query.data ? toProcedureDetail(query.data) : null),
    [query.data]
  );

  return {
    procedure,
    artifact: query.data ?? null,
    isLoading: query.isPending,
    error: query.isError ? 'Unable to load this procedure.' : null,
    notFound: query.error instanceof ApiError && query.error.status === 404,
  };
}
